<template>
  <div class="input-group">
    <input type="date"
           v-if="type === 'datetime' || type === 'date'"
           :id="id"
           :value="dateValue"
           :min="min"
           :class="[
              'form-control',
              className ?? '',
           ]"
           style="flex-grow: 3"
           @input="updateDate($event.target.value)"
           :disabled="disabled"
           :required="required"
    />
    <input type="time"
           v-if="type === 'datetime' || type === 'time'"
           :value="timeValue"
           :step="minuteStep * 60"
           :class="[
              'form-control',
              className ?? '',
           ]"
           style="width: 58px"
           @input="updateTime($event.target.value)"
           :disabled="disabled"
           :required="required"
    />
    <div class="input-group-append" v-if="withTimezone">
      <span class="input-group-text">{{ timezone }}</span>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import dayjs from "dayjs";

// Définition des props et des émissions
const props = defineProps({
  id: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'datetime',
    validator(value, props) {
      return ['datetime', 'date', 'time'].includes(value);
    }
  },
  timezone: {
    type: String,
    default: 'UTC',
  },
  modelValue: {
    type: [String, Date, dayjs],
    default: ''
  },
  min: {
    type: String,
  },
  minuteStep: {
    type: Number,
    default: 1,
  },
  className: {
    type: String,
    default: '',
  },
  withTimezone: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:modelValue',
]);

const initialFormatType = ref(null);
const setInitialFormatType = (value) => {
  if (value === null || value === undefined || value === '') {
    initialFormatType.value = null;
  } else if (dayjs.isDayjs(value)) {
    initialFormatType.value = 'dayjs';
  } else if (value instanceof Date) {
    initialFormatType.value = 'date';
  } else {
    initialFormatType.value = 'string';
  }
};
onMounted(() => {
  setInitialFormatType(props.modelValue);
});

const normalizeDate = (date) => {
  // console.log('normalizeDate (input) --', date);
  if (! date) {
    return null;
  }
  if (dayjs.isDayjs(date)) {
    date = date.tz(props.timezone).toDate();
  }
  if (! (date instanceof Date)) {
    date = dayjs(date).tz(props.timezone).toDate();
  }

  // console.log('normalizeDate', date);

  return date instanceof Date ? date : null;
}

// Fonctions de formatage
const formatDate = (date) => {
  const normalizedDate = normalizeDate(date);
  if (! normalizedDate) {
    return '';
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: props.timezone, // Fuseau horaire
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  const formattedDate = formatter.format(normalizedDate);
  const [year, month, day] = formattedDate.split('/').reverse(); // Dépend de la locale utilisée, ici pour 'fr-CA'
  return `${year}-${month}-${day}`;
};

const formatTime = (date) => {
  const normalizedDate = normalizeDate(date);
  if (! normalizedDate) {
    return '';
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: props.timezone, // Fuseau horaire
    hour: '2-digit',
    minute: '2-digit',
    hour12: false // Format 24 heures
  });

  const formattedTime = formatter.format(normalizedDate);
  // Formatter peut retourner des valeurs avec des espaces ou des caractères non numériques,
  // il faut donc normaliser le format pour un <input type="time">
  return formattedTime.replace(/\s/g, '').substring(0, 5); // Garde uniquement HH:mm
};

// Références réactives pour la date et l'heure
const dateValue = ref(props.modelValue ? formatDate(props.modelValue) : null);
const timeValue = ref(props.modelValue ? formatTime(props.modelValue) : null);

// Observer le value pour des changements externes
watch(() => props.modelValue, (newValue) => {
  // console.log('watch value change', newValue);
  dateValue.value = formatDate(newValue);
  timeValue.value = formatTime(newValue);
  setInitialFormatType(newValue);
});

watch(() => props.timezone, () => {
  // console.log('watch value change', newValue);
  dateValue.value = formatDate(props.modelValue);
  timeValue.value = formatTime(props.modelValue);
});


// Fonctions de mise à jour
const updateDate = (newDate) => {
  dateValue.value = newDate;

  const dateArray = newDate.split('-');
  if (dateArray.length < 3) {
    emitUpdate('');
  }

  const [year, month, day] = dateArray;
  if (year === undefined || year.startsWith('0') || month === undefined || day === undefined) {
    return;
  }

  const time = props.type === 'date' ? '00:00' : (timeValue.value ?? '00:00');
  const updatedDate = dayjs.tz(newDate + ' ' + time + ':00', props.timezone);

  emitUpdate(updatedDate.toDate());
};

const updateTime = (newTime) => {
  timeValue.value = newTime;

  const [hours, minutes] = newTime.split(':');
  if (hours === undefined || minutes === undefined) {
    return;
  }

  const updatedDate = dayjs.tz(dateValue.value + ' ' + newTime +':00', props.timezone);

  emitUpdate(updatedDate.toDate());
};

const emitUpdate = (updatedDate) => {
  console.log('emit update', typeof  updatedDate, initialFormatType.value)
  if (! updatedDate) {
    emit('update:modelValue', null);
    return;
  }

  if (! initialFormatType.value || initialFormatType.value === 'string') {
    // console.log('emit update with string format', updatedDate)
    updatedDate = dayjs(updatedDate).utc().toISOString();
  } else if (initialFormatType.value === 'date') {
    // console.log('emit update with Date format', updatedDate)
    updatedDate = dayjs(updatedDate).utc().toDate();
  } else if (initialFormatType.value === 'dayjs') {
    // console.log('emit update with dayJS format', updatedDate)
    updatedDate = dayjs(updatedDate).utc();
  }

  emit('update:modelValue', updatedDate);
};


</script>
